import React, { useState, useEffect } from 'react';
import { Box } from 'grommet';
import { authorizedFetch } from '../utils';
import { FTSelect } from './inputs';

const NO_VERTICAL = {
  id: '',
  name: 'No verticals found'
};

export const getCampaignVerticals = async () => {
  return await authorizedFetch(`/api/vertical/getAll`, 'GET').then(response => {
    return response ?? [];
  });
};

const VerticalSelector = ({
  verticalId = '',
  subVerticalId = '',
  onChange,
  disabled,
  verticalTipContent = 'Select the market vertical that best categorizes this offer. Manage Vertical rules independently within each partner’s traffic source settings.',
  subVerticalTipContent = 'After selecting a vertical, you can further categorize this offer by selecting a subvertical.',
  addError
}) => {
  const [verticals, setVerticals] = useState([]);
  const [filteredVerticals, setFilteredVerticals] = useState([]);
  const [filteredSubverticals, setFilteredSubverticals] = useState([]);

  // Derived every render, not mirrored into state - a prior version only
  // refreshed "selectedVertical" from this component's own Vertical onChange
  // (or the mount fetch), so setting verticalId another way (e.g. Schedule
  // Mode's projection patching the draft item directly) left Subvertical
  // stuck on stale/empty options even with a valid subVerticalId. Deriving
  // inline makes it follow verticalId however it's set, same as Vertical's
  // own value below already does.
  const selectedVertical = verticalId
    ? verticals.find(v => v.id === verticalId)
    : undefined;
  const subverticals = selectedVertical?.subVerticals ?? [];

  useEffect(() => {
    getCampaignVerticals()
      .then(fetchedVerticals => {
        if (Array.isArray(fetchedVerticals) && fetchedVerticals.length > 0) {
          setVerticals([...fetchedVerticals]);
          setFilteredVerticals([...fetchedVerticals]);

          const loadedVertical = fetchedVerticals.find(
            v => v.id === verticalId
          );

          if (!loadedVertical && verticalId?.length > 0) {
            onChange({ verticalId: null, subVerticalId: null });
            addError(
              `A vertical with an unmatched ID was found. Please select a new vertical.`
            );
          }
        } else {
          setVerticals(
            verticalId
              ? [{ id: verticalId, name: 'No verticals found' }]
              : [NO_VERTICAL]
          );
          setFilteredVerticals(
            verticalId
              ? [{ id: verticalId, name: 'No verticals found' }]
              : [NO_VERTICAL]
          );
        }
      })
      .catch(err => {
        addError(`There was an error fetching campaign verticals`, err.message);
        return null;
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFilteredSubverticals(subverticals);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVertical]);

  return (
    <Box gap='small'>
      <FTSelect
        disabled={disabled}
        title={`Vertical`}
        placeholder={`No vertical selected`}
        labelKey='name'
        valueKey='id'
        options={filteredVerticals}
        value={selectedVertical}
        label={'Vertical'}
        tipContent={verticalTipContent}
        onChange={({ id }) => onChange({ verticalId: id, subVerticalId: null })}
        handleOpen={() => {
          setFilteredVerticals(verticals);
        }}
        handleSearch={search => {
          const lowercaseSearch = search.toLowerCase();
          if (search === '') {
            setFilteredVerticals(verticals);
          } else {
            setFilteredVerticals(
              verticals.filter(v => {
                const lowercaseLabel = v.name.toLowerCase();
                return lowercaseLabel.includes(lowercaseSearch);
              })
            );
          }
        }}
      />
      <FTSelect
        disabled={!selectedVertical}
        title={`Subvertical`}
        placeholder={
          !!selectedVertical
            ? `No subvertical selected`
            : 'Select a vertical first'
        }
        labelKey='name'
        valueKey='id'
        options={filteredSubverticals}
        value={subverticals.find(u => u.id === subVerticalId)}
        label={'Subvertical'}
        tipContent={subVerticalTipContent}
        onChange={({ id }) => onChange({ subVerticalId: id })}
        handleOpen={() => {
          setFilteredSubverticals(subverticals);
        }}
        handleSearch={search => {
          const lowercaseSearch = search.toLowerCase();
          if (search === '') {
            setFilteredSubverticals(subverticals);
          } else {
            setFilteredSubverticals(
              subverticals.filter(sv => {
                const lowercaseLabel = sv.name.toLowerCase();
                return lowercaseLabel.includes(lowercaseSearch);
              })
            );
          }
        }}
      />
    </Box>
  );
};

export default VerticalSelector;
